import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { } from 'react';

interface RuleImpactEntry {
  ruleFamily: string;
  traceType: string;
  callSite: string | null;
  rowCount: number;
  aggregates: Record<string, number>;
}

// One renderer per known traceType — this is the "not free" part of extensibility the
// design spec calls out: storage is generic, but each trace_type's payload shape needs
// its own plain-English mapping. Adding a new rule family means adding an entry here,
// not just a new emission site.
export const CARD_RENDERERS: Record<string, {
  title: string;
  render: (agg: Record<string, number>, rowCount: number) => string;
}> = {
  cmp_sparse_drop: {
    title: 'Comparator sufficiency',
    render: (agg, rowCount) => {
      const avgFinal = rowCount > 0 ? (agg.finalComparators || 0) / rowCount : 0;
      // codex review on #5536: this used to be (SUM(shortfallVsTarget) / rowCount) * 100,
      // labeled "% of tables below target" — but shortfallVsTarget is a per-table comparator
      // COUNT, not a boolean, so that formula computes "average shortfall x 100" (one table
      // short by 5 comparators alone would read "500%"), not a percentage of tables. The
      // aggregate query intentionally has no per-trace-type conditional counting (it SUMs
      // whatever numeric keys are present, generically, across all trace types), so getting a
      // real "% of tables below target" would mean adding a trace-type-specific COUNT WHERE to
      // the shared query — reporting the true average shortfall instead keeps the aggregate
      // generic and is what the underlying number actually measures.
      const avgShortfall = rowCount > 0 ? (agg.shortfallVsTarget || 0) / rowCount : 0;
      // avgShortfall is averaged across ALL tables (including ones with zero shortfall), not
      // just the subset that fell short — say so explicitly, so this copy can't drift into the
      // same "conditioned-on-a-subset-but-computed-over-everything" mismatch as the bug above.
      return `You asked for up to 5 comparators; on average this run found and safely displayed ${avgFinal.toFixed(1)}. `
        + `Averaged across every table (including ones that hit the target), tables were short by ${avgShortfall.toFixed(1)} comparators.`;
    },
  },
  cmp_completeness: {
    title: 'Comparator completeness',
    render: (agg) => {
      const items = agg.items || 0;
      const dropped = (agg.droppedMissingValue || 0) + (agg.droppedNoAttributeClaimed || 0);
      const rate = items > 0 ? (dropped / items) * 100 : 0;
      return `${rate.toFixed(0)}% of comparator claims were left out because we didn't have a grounded value — not because of a bug.`;
    },
  },
  cmp_quota: {
    title: 'FAQ comparator quota',
    render: (agg) => {
      const seen = agg.comparators || 0;
      const kept = agg.kept || 0;
      return `Comparators available vs. shown after the FAQ slot quota: ${seen.toFixed(0)} available, ${kept.toFixed(0)} shown.`;
    },
  },
  cmp_faq: {
    title: 'FAQ comparator eligibility',
    render: (agg) => {
      const kept = agg.comparatorsKept || 0;
      const total = agg.comparators || 0;
      const droppedTotal = total - kept;
      return `${kept.toFixed(0)} of ${total.toFixed(0)} comparator FAQ candidates were kept; ${droppedTotal.toFixed(0)} were dropped (absent, empty, or unmatched data) — this is why some products get fewer or zero comparison FAQs.`;
    },
  },
};

export function RuleImpactSection({ ruleImpact }: { ruleImpact: RuleImpactEntry[] }) {
  const withRows = ruleImpact.filter((entry) => entry.rowCount > 0 && CARD_RENDERERS[entry.traceType]);

  if (withRows.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Rule Impact</CardTitle></CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">No rule-impact data for this run.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {withRows.map((entry) => {
        const renderer = CARD_RENDERERS[entry.traceType];
        return (
          <Card key={`${entry.traceType}-${entry.callSite ?? 'none'}`}>
            <CardHeader><CardTitle className="text-base">{renderer.title}</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm">{renderer.render(entry.aggregates, entry.rowCount)}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
